import type { DelegateFormData } from '../types';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Typography, Form, Divider } from 'antd';

import themeToken from '@lib/theme/tokens/index';
import { StyledButton } from '@/lib/theme/components/Button';
import { StyledForm } from '@/components/Shared/Form';
import { useSelectedCompanyStore } from '@/stores/selectedCompany';
import CustomizeRequiredMark from '@/components/Shared/CustomizeRequiredMark';
import { useTrackEvent } from '@/hooks/useTrackEvents';
import CommonDropDownField from '@/components/Shared/CommonDropDownField';

import { AttendeesInclusion } from '../utils/enums';
import { useGetMeetingAttendees } from '../api/meetings/getMeetingAttendees';
import { useDelegateVoteToUser } from '../api/meetings/delegateVoteToUser';
import { useUserDataStore } from '@/stores/UserData';

const { Title } = Typography;

type Props = {
  handleCancel?: () => void;
  handleOk?: (equityID?: number) => void;
  selectedMeetingId?: number;
};

const DelegateForm = ({ handleCancel, handleOk, selectedMeetingId }: Props) => {
  const { sendMixpanelEvent } = useTrackEvent({ page: 'delegate-vote-modal' });
  const { t } = useTranslation('investorRelations', {
    keyPrefix: 'meetings',
  });

  const { selectedCompany } = useSelectedCompanyStore((state) => state);
  const { userData } = useUserDataStore((state) => state);

  const [form] = Form.useForm();
  const users_inclusion = Form.useWatch<AttendeesInclusion>('users_inclusion', form);

  const { mutate: mutateDelegateVote, isPending: isDelegateVotePending } = useDelegateVoteToUser({
    postSuccess: () => {
      handleOk?.();
    },
  });

  const [attendeesListPage, setAttendeesListPage] = useState(1);
  const [attendeesListHasNextPage, setAttendeesListHasNextPage] = useState(true);
  const [attendeesSearchQuery, setAttendeesSearchQuery] = useState<string>();

  const { data: meetingAttendees, isLoading: isGetMeetingAttendeesLoading } = useGetMeetingAttendees({
    companyID: selectedCompany?.cid as number,
    meetingID: selectedMeetingId ? +selectedMeetingId : -1,
    queryParams: attendeesSearchQuery
      ? {
          page: attendeesListPage,
          q: attendeesSearchQuery,
        }
      : { page: attendeesListPage },
  });

  const cancelClickHandler = () => {
    sendMixpanelEvent('back-btn-clicked');
    form.resetFields();
    handleCancel?.();
  };

  const onFinish = (values: DelegateFormData) => {
    const { meetingAttendeeID } = values;
    sendMixpanelEvent('submit-btn-clicked');

    const body = {
      meetingAttendeeID,
    };

    mutateDelegateVote({
      companyID: selectedCompany?.cid as number,
      meetingID: selectedMeetingId as number,
      body,
    });
  };

  const attendeesOptions = useMemo(() => {
    const filtered = meetingAttendees?.data.filter(
      (attendee) => (userData?.userInfo?.uid ? +userData?.userInfo?.uid : -1) !== attendee?.company_user?.uid,
    );

    return filtered?.map((attendee) => {
      return {
        name: attendee.company_user.name,
        value: attendee.id,
        id: attendee.id,
      };
    });
  }, [meetingAttendees, userData]);

  return (
    <StyledForm
      layout="vertical"
      form={form}
      onFinish={onFinish}
      size="large"
      requiredMark={CustomizeRequiredMark}
      disabled={isGetMeetingAttendeesLoading}
    >
      <Flex align="start" vertical>
        <Title
          level={5}
          style={{
            color: themeToken['branding-primary-6'],
          }}
        >
          {t('delegateVoteToUser')}
        </Title>
        <Divider
          style={{
            marginBlock: themeToken.margin,
            borderColor: themeToken['branding-natural-3'],
          }}
        />
        <Form.Item
          label={t('selectDelegate')}
          name="meetingAttendeeID"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          rules={[{ required: users_inclusion !== AttendeesInclusion.ALL, message: 'Please select attendees' }]}
          style={{
            marginBlock: 0,
          }}
        >
          <CommonDropDownField
            placeholder={t('selectDelegate')}
            isLoading={isGetMeetingAttendeesLoading}
            data={attendeesOptions}
            metadata={meetingAttendees?.metadata}
            optionsListPage={attendeesListPage}
            setOptionsListPage={setAttendeesListPage}
            optionsHasNextPage={attendeesListHasNextPage}
            labelKey="name"
            formItemName="meetingAttendeeID"
            setOptionsHasNextPage={setAttendeesListHasNextPage}
            setOptionsSearchQuery={setAttendeesSearchQuery}
            // mode="multiple"
          />
        </Form.Item>
        <Divider
          style={{
            marginBlock: themeToken.margin,
            borderColor: themeToken['branding-natural-3'],
            marginBlockStart: 'auto',
          }}
        />
        <Flex style={{ width: '100%' }} justify="space-between">
          <StyledButton ghost type="primary" onClick={cancelClickHandler} size="middle">
            {t('back')}
          </StyledButton>
          <StyledButton loading={isDelegateVotePending} type="primary" htmlType="submit" size="middle">
            {t('save')}
          </StyledButton>
        </Flex>
      </Flex>
    </StyledForm>
  );
};

export default DelegateForm;
